import { z } from 'zod';

/**
 * Zod schemas for BP-14 §5 (ARCHITECTURE §3 Validation).
 *
 * Hand-written, for the reason `features/accounting/schemas.ts` gives:
 * `lib/api/schema.d.ts` is regenerated in CI, and a screen compiled against
 * it would track whatever was last exported rather than the contract this
 * feature was written to.
 *
 * **Every amount is paise, an integer.** There is no money here that is not
 * a `number` holding paise, and `rupees()` is the only place it becomes a
 * string with a decimal point in it.
 */

/** `wallet_ledger.kind` — the six ways money moves. */
export const ledgerKinds = ['topup', 'bonus', 'redeem', 'refund', 'expire', 'adjust'] as const;
export type LedgerKind = (typeof ledgerKinds)[number];

export const membershipPeriods = ['monthly', 'quarterly', 'yearly'] as const;
export type MembershipPeriod = (typeof membershipPeriods)[number];

export const membershipStatuses = ['active', 'expired', 'cancelled'] as const;

export const packageSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().default(null),
  price_paise: z.number().int(),
  validity_days: z.number().int(),
  sac_code: z.string(),
  gst_rate_bp: z.number().int(),
  active: z.boolean(),
  sort_order: z.number().int(),
});
export type PrepaidPackage = z.infer<typeof packageSchema>;
export const packageListSchema = z.array(packageSchema);

export const planSchema = z.object({
  id: z.string(),
  name: z.string(),
  price_paise: z.number().int(),
  period: z.enum(membershipPeriods),
  discount_bp: z.number().int(),
  wallet_bonus_paise: z.number().int(),
  sac_code: z.string(),
  gst_rate_bp: z.number().int(),
  active: z.boolean(),
});
export type MembershipPlan = z.infer<typeof planSchema>;
export const planListSchema = z.array(planSchema);

export const creditSchema = z.object({
  credit_id: z.string(),
  purchase_id: z.string(),
  service_id: z.string(),
  /** Decimals on the wire, because a pro-rata refund divides a quantity. */
  qty_total: z.string(),
  qty_used: z.string(),
  qty_remaining: z.string(),
  expires_at: z.string(),
});
export type Credit = z.infer<typeof creditSchema>;
export const creditListSchema = z.array(creditSchema);

export const purchaseSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  package_id: z.string(),
  invoice_id: z.string(),
  purchased_at: z.string(),
  expires_at: z.string(),
  status: z.string(),
  price_paise: z.number().int(),
});
export type Purchase = z.infer<typeof purchaseSchema>;

export const membershipSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  plan_id: z.string(),
  starts_on: z.string(),
  ends_on: z.string(),
  status: z.enum(membershipStatuses),
  invoice_id: z.string(),
  benefits_used: z.number().int(),
});
export type Membership = z.infer<typeof membershipSchema>;
/** The api answers `null` for a client with none: a fact, not an error. */
export const membershipOrNullSchema = membershipSchema.nullable();

export const ledgerRowSchema = z.object({
  id: z.string(),
  kind: z.enum(ledgerKinds),
  /** Signed: positive adds, negative takes away, and the sum is the balance. */
  amount_paise: z.number().int(),
  ref_type: z.string().nullable(),
  ref_id: z.string().nullable(),
  expires_at: z.string().nullable(),
  reason: z.string().nullable(),
  created_at: z.string(),
});
export type LedgerRow = z.infer<typeof ledgerRowSchema>;

export const walletSchema = z.object({
  client_id: z.string(),
  balance_paise: z.number().int(),
  /** What the client's own screen warns about, computed as the job computes it. */
  expiring_soon_paise: z.number().int(),
  expiring_on: z.string(),
  history: z.array(ledgerRowSchema),
});
export type Wallet = z.infer<typeof walletSchema>;

export const topUpResultSchema = z.object({
  amount_paise: z.number().int(),
  bonus_paise: z.number().int(),
});
export const refundResultSchema = z.object({ amount_paise: z.number().int() });
export const redeemResultSchema = z.object({ total_paise: z.number().int() });

/**
 * Paise as rupees.
 *
 * The one place in this feature where money becomes a string. A screen that
 * formatted it inline would eventually divide by a hundred twice, and a bill
 * that said ₹5 about ₹500 is the worst thing this feature could print.
 */
export function rupees(paise: number): string {
  return `₹${(paise / 100).toFixed(2)}`;
}
